import React, { createContext, useContext, useState, useEffect } from 'react';

const PersonaContext = createContext();

export const PERSONAS = {
  EXECUTIVE: 'executive',
  PORTFOLIO_MANAGER: 'portfolio-manager',
  PROGRAM_MANAGER: 'program-manager',
  PROJECT_MANAGER: 'project-manager'
};

export const PERSONA_CONFIG = {
  [PERSONAS.EXECUTIVE]: {
    label: 'Executive',
    defaultRoute: '/dashboard/executive',
    defaultZoom: 'quarter',
    icon: 'Building2'
  },
  [PERSONAS.PORTFOLIO_MANAGER]: {
    label: 'Portfolio Manager',
    defaultRoute: '/dashboard/portfolio-manager',
    defaultZoom: 'month',
    icon: 'Briefcase'
  },
  [PERSONAS.PROGRAM_MANAGER]: {
    label: 'Program Manager',
    defaultRoute: '/dashboard/program-manager',
    defaultZoom: 'month',
    icon: 'FolderKanban'
  },
  [PERSONAS.PROJECT_MANAGER]: {
    label: 'Project Manager',
    defaultRoute: '/dashboard/project-manager',
    defaultZoom: 'sprint',
    icon: 'FileSpreadsheet'
  }
};

export const usePersona = () => {
  const context = useContext(PersonaContext);
  if (!context) {
    throw new Error('usePersona must be used within a PersonaProvider');
  }
  return context;
};

export const PersonaProvider = ({ children }) => {
  const [persona, setPersona] = useState(() => {
    const saved = localStorage.getItem('simpliview_persona');
    return saved || PERSONAS.EXECUTIVE;
  });
  
  const [timelineZoom, setTimelineZoom] = useState(() => {
    const saved = localStorage.getItem('simpliview_timeline_zoom');
    return saved || PERSONA_CONFIG[PERSONAS.EXECUTIVE].defaultZoom;
  });

  useEffect(() => {
    localStorage.setItem('simpliview_persona', persona);
    // Update timeline zoom when persona changes
    const config = PERSONA_CONFIG[persona];
    if (config) {
      setTimelineZoom(config.defaultZoom);
    }
  }, [persona]);

  useEffect(() => {
    localStorage.setItem('simpliview_timeline_zoom', timelineZoom);
  }, [timelineZoom]);

  const switchPersona = (newPersona) => {
    if (PERSONAS[newPersona.toUpperCase().replace(/-/g, '_')] || Object.values(PERSONAS).includes(newPersona)) {
      setPersona(newPersona);
    }
  };

  const getDefaultRoute = () => {
    return PERSONA_CONFIG[persona]?.defaultRoute || '/';
  };

  const getCurrentConfig = () => {
    return PERSONA_CONFIG[persona] || PERSONA_CONFIG[PERSONAS.EXECUTIVE];
  };

  return (
    <PersonaContext.Provider value={{
      persona,
      setPersona: switchPersona,
      timelineZoom,
      setTimelineZoom,
      getDefaultRoute,
      getCurrentConfig,
      PERSONAS,
      PERSONA_CONFIG
    }}>
      {children}
    </PersonaContext.Provider>
  );
};
